import React, { useState, useEffect, useRef } from "react";
import LoadingOverlay from "../../../../common/LoadingBar.js";
import loader from "../../../../../assets/Image/Loader2.gif";

const ReusableTable = ({
  columns = [],
  fetchData,
  handlePrint,
  showAction = false,
  showExport = false,
  showFilter = false,
  enablePagination = true,
  togglePaymentModal,
  onEdit,
  onDelete,
  onSearchChange,
  rowsPerPage = 10,
  showFooter = true,
  searchData,
  renderExpandedRow,
  isRowExpandable,
  showSearch = true,
}) => {
  const [data, setData] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });
  const [showDropdown, setShowDropdown] = useState(false);

  const searchTimer = useRef();

  const sortData = (items, sortKey, sortDirection) => {
    if (!sortKey) return items;

    return [...items].sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];

      if (aVal == null) return 1;
      if (bVal == null) return -1;

      if (typeof aVal === "string") {
        return sortDirection === "ascending"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      if (typeof aVal === "number" || typeof aVal === "boolean") {
        return sortDirection === "ascending" ? aVal - bVal : bVal - aVal;
      }

      return 0;
    });
  };

  const loadData = async (page = 1, search = searchData) => {
    setLoading(true);
    try {
      const res = await fetchData({
        page,
        searchTerm: search,
        rowsPerPage,
      });

      const rawData = res?.data || [];
      const total = res?.totalPages || 1;
      const sorted = sortData(rawData, sortConfig.key, sortConfig.direction);

      setData(sorted);
      setTotalPages(total);
    } catch (err) {
      console.error("Table fetch failed", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(currentPage, searchTerm);
  }, [currentPage, rowsPerPage]);

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setCurrentPage(1);
      loadData(1, searchTerm);
    }, 500);

    return () => clearTimeout(searchTimer.current);
  }, [searchTerm, rowsPerPage]);

  useEffect(() => {
    if (onSearchChange) {
      onSearchChange(searchTerm);
    }
  }, [searchTerm, onSearchChange]);

  const handleSort = (field) => {
    let direction = "ascending";
    if (sortConfig.key === field && sortConfig.direction === "ascending") {
      direction = "descending";
    }

    const sorted = sortData(data, field, direction);
    setSortConfig({ key: field, direction });
    setData(sorted);
  };

  const downloadCSV = () => {
    const headers = columns.map((col) => col.header).join(",") + "\n";
    const rows = data.map((row) =>
      columns.map((col) => row[col.field] || "").join(",")
    );
    const csv = headers + rows.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "export.csv";
    link.click();
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxPages = 5;
    let start = Math.max(1, currentPage - Math.floor(maxPages / 2));
    let end = Math.min(totalPages, start + maxPages - 1);

    if (end - start < maxPages - 1) {
      start = Math.max(1, end - maxPages + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(
        <li
          key={i}
          className={`page-item ${currentPage === i ? "active" : ""}`}
        >
          <a
            className="page-link"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setCurrentPage(i);
            }}
          >
            {i}
          </a>
        </li>
      );
    }

    return pages;
  };

  const canExpandRow = (item) => {
    if (typeof renderExpandedRow !== "function") return false;
    if (typeof isRowExpandable === "function") return isRowExpandable(item);
    return true;
  };

  const getRowKey = (item, index) => item.id || item.invoice_no || index;

  const toggleRow = (item, index) => {
    if (!canExpandRow(item)) return;

    const rowKey = getRowKey(item, index);
    setExpandedRows((prev) => ({
      ...prev,
      [rowKey]: !prev[rowKey],
    }));
  };

  const tableColSpan = columns.length + 1 + (showAction ? 1 : 0) + (renderExpandedRow ? 1 : 0);

  return (
    <div className="data-table-container">
      {showSearch && (
        <div className="d-flex mb-3">
          <div className="expense-searchcontainerstart d-flex">
            <div className="search-container">
              <input
                type="text"
                style={{
                  width: "300px",
                  height: "40px",
                  backgroundColor: "#fff",
                  border: "1px solid #DBDBDB",
                  boxShadow: "0px 0px 10px rgba(187, 187, 187, 0.25)",
                }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search"
              />
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <table
        className="data-table custom-table"
        style={{
          borderRadius: "20px 20px 0 0",
          border: "1px solid #EBEAF2",
          borderCollapse: "separate",
          borderSpacing: 0,
          overflow: "hidden",
        }}
      >
        <thead className="table-header">
          <tr>
            {renderExpandedRow && <th scope="col" style={{ width: "48px" }}></th>}
            <th scope="col" className="no-column">No</th>
            {columns.map((col, index) => (
              <th key={index} scope="col" onClick={() => handleSort(col.field)}>
                {col.header}
                {sortConfig.key === col.field ? (
                  sortConfig.direction === "ascending" ? (
                    <span style={{ marginLeft: 5 }}>▲</span>
                  ) : (
                    <span style={{ marginLeft: 5 }}>▼</span>
                  )
                ) : (
                  <span style={{ marginLeft: 5, opacity: 0.3 }}>⇅</span>
                )}
              </th>
            ))}
            {showAction && <th scope="col" className="action-column">Action</th>}
          </tr>
        </thead>

        <tbody className="table-body">
          {loading ? (
            <tr>
              <td
                colSpan={tableColSpan}
                style={{ textAlign: "center", padding: "20px" }}
              >
      <LoadingOverlay loading={loading} loader={loader} />
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td
                colSpan={tableColSpan}
                style={{
                  textAlign: "center",
                  fontWeight: "600",
                  fontSize: "24px",
                  background:
                    "linear-gradient(180deg, rgba(255, 255, 255, 0.1) -92.86%, #4A5546 71.43%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  padding: "40px",
                }}
              >
                No data found
              </td>
            </tr>
          ) : (
            data.map((item, index) => {
              const rowKey = getRowKey(item, index);
              const rowCanExpand = canExpandRow(item);
              const isExpanded = !!expandedRows[rowKey];

              return (
                <React.Fragment key={rowKey}>
                  <tr
                    onClick={() => toggleRow(item, index)}
                    className={rowCanExpand ? "expandable-table-row" : ""}
                    style={rowCanExpand ? { cursor: "pointer" } : undefined}
                  >
                    {renderExpandedRow && (
                      <td>
                        {rowCanExpand && (
                          <button
                            type="button"
                            className="expand-row-button"
                            onClick={(event) => {
                              event.stopPropagation();
                              toggleRow(item, index);
                            }}
                            aria-label={isExpanded ? "Collapse row" : "Expand row"}
                          >
                            {isExpanded ? "-" : "+"}
                          </button>
                        )}
                      </td>
                    )}
                    <td>{(currentPage - 1) * rowsPerPage + index + 1}</td>
                    {columns.map((col, colIndex) => (
                      <td key={colIndex}>
                        {typeof col.body === "function" ? (
                          col.body(item, { rowIndex: index, field: col.field })
                        ) : col.field === "status" ? (
                          <div className={`status ${item[col.field]}`}>{item[col.field]}</div>
                        ) : col.field === "pay_salary" ? (
                          <button
                            className="btn btn-primary"
                            onClick={(event) => {
                              event.stopPropagation();
                              togglePaymentModal(item);
                            }}
                          >
                            Pay Salary
                          </button>
                        ) : (
                          item[col.field]
                        )}
                      </td>
                    ))}
                    {showAction && (
                      <td className="action-column">
                        {handlePrint && (
                          <button
                            className="action-button"
                            onClick={(event) => {
                              event.stopPropagation();
                              handlePrint(item.id, true);
                            }}
                            title="Print Invoice"
                          >
                            🖨️
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                  {isExpanded && (
                    <tr className="expanded-table-detail-row">
                      <td colSpan={tableColSpan}>
                        {renderExpandedRow(item, { rowIndex: index })}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })
          )}
        </tbody>
      </table>

    
      {enablePagination && data.length > 0 && (
        <nav>
          <ul className="pagination">{renderPagination()}</ul>
        </nav>
      )}
    </div>
  );
};

export default ReusableTable;
